import React from "react";

/**
 * RevisionCloud — drawing-native revision-cloud overlay.
 *
 * Renders a scalloped (revision-cloud style) outline around a normalised
 * rectangle, in the colour family that matches the change category:
 *
 *   - added    → soft red,    cloud outline + faint fill
 *   - removed  → restrained blue, cloud outline + faint fill
 *   - modified → warm amber,  cloud outline + diagonal hatch fill
 *   - unchanged → not rendered (we don't paint unchanged regions on the
 *     drawing — that would defeat the legibility goal).
 *
 * The cloud is built from a series of arcs walking around the bounding
 * rectangle. Arc radius is keyed to the SHORT side of the rectangle so it
 * looks proportionate at any sheet zoom. Units: viewBox 0..100 normalised
 * (parent SVG should pass `viewBox="0 0 100 100" preserveAspectRatio="none"`).
 */
export default function RevisionCloud({
  region,        // { nx, ny, nw, nh, type }
  index,         // 1-based — rendered as a small index chip
  focused,       // boolean — bumps stroke + adds drop shadow
  onClick,
  testid,
}) {
  if (!region) return null;
  const x = region.nx * 100;
  const y = region.ny * 100;
  const w = region.nw * 100;
  const h = region.nh * 100;
  const palette = CLOUD_PALETTE[region.type] || CLOUD_PALETTE.modified;
  const path = scallopPath(x, y, w, h);
  const patternId = `hatch-${region.type}-${index}`;
  const fillSpec = region.type === "modified" ? `url(#${patternId})` : palette.fill;
  const stroke = palette.stroke;
  const strokeWidth = focused ? 0.5 : 0.32;

  return (
    <g
      onClick={onClick}
      data-testid={testid}
      data-viewer-overlay="1"
      data-overlay-type="revision-cloud"
      data-overlay-anchor-nx={region.nx}
      data-overlay-anchor-ny={region.ny}
      data-diff-type={region.type}
      data-focused={focused ? "true" : "false"}
      style={{ cursor: onClick ? "pointer" : "default" }}
    >
      {region.type === "modified" && (
        <defs>
          <pattern id={patternId} patternUnits="userSpaceOnUse" width="1.2" height="1.2" patternTransform="rotate(45)">
            <line x1="0" y1="0" x2="0" y2="1.2" stroke={palette.hatch} strokeWidth="0.4" />
          </pattern>
        </defs>
      )}
      <path
        d={path}
        fill={fillSpec}
        stroke={stroke}
        strokeWidth={strokeWidth}
        strokeLinejoin="round"
        vectorEffect="non-scaling-stroke"
        style={focused ? { filter: `drop-shadow(0 0 4px ${palette.glow})` } : undefined}
      />
      {/* Index chip — small numbered badge anchored to top-left of region.
          Drawing-native style: solid stroke colour with white digit. */}
      {index != null && (
        <g transform={`translate(${x + 0.4}, ${y + 0.4})`}>
          <circle r="1.6" cx="1.6" cy="1.6" fill={stroke} stroke="#0a0a0b" strokeWidth="0.18" vectorEffect="non-scaling-stroke" />
          <text
            x="1.6" y="1.6"
            fill="#0a0a0b"
            fontSize="2"
            fontWeight="700"
            textAnchor="middle"
            dominantBaseline="central"
            fontFamily="ui-monospace, SFMono-Regular, Menlo, monospace"
            style={{ userSelect: "none", pointerEvents: "none" }}
          >
            {index}
          </text>
        </g>
      )}
    </g>
  );
}


/**
 * scallopPath — generate a closed SVG path that walks around the bounding
 * rect with small outward arcs (the classic engineer's revision cloud).
 *
 * Strategy: divide each side into N segments of length `arcLen`, draw an
 * outward arc per segment. Radius is tuned so the bumps look proportionate
 * to the short side of the rectangle.
 */
function scallopPath(x, y, w, h) {
  const shortSide = Math.min(w, h);
  // Scallop radius — restrained so the cloud reads as engineering, not
  // decorative. Clamped to a small range so very small / very large
  // regions still get visually sensible bumps.
  const r = Math.max(0.6, Math.min(1.6, shortSide * 0.12));
  const arcLen = r * 2;

  // Helper — sweep along a straight side with outward scallops.
  const sideArcs = (sx, sy, dx, dy, length) => {
    const segs = Math.max(1, Math.round(length / arcLen));
    const stepX = (dx * length) / segs;
    const stepY = (dy * length) / segs;
    let out = "";
    let cx = sx;
    let cy = sy;
    for (let i = 0; i < segs; i++) {
      cx += stepX;
      cy += stepY;
      // Arc command — sweep flag 1 = outward bump.
      out += ` A ${r} ${r} 0 0 1 ${cx.toFixed(3)} ${cy.toFixed(3)}`;
    }
    return out;
  };

  // Start top-left, walk clockwise.
  let d = `M ${x} ${y}`;
  d += sideArcs(x, y,  1,  0, w);
  d += sideArcs(x + w, y,  0,  1, h);
  d += sideArcs(x + w, y + h, -1,  0, w);
  d += sideArcs(x, y + h,  0, -1, h);
  d += " Z";
  return d;
}


/**
 * Drawing-native, restrained palette. Saturation kept LOW — these overlays
 * sit on top of black-on-white plan rasters and must NEVER fight the
 * drawing for visual weight. Inspired by hand-drawn red/blue clouding
 * conventions in printed addenda.
 */
const CLOUD_PALETTE = {
  added: {
    stroke: "rgba(239, 68, 68, 0.92)",   // soft red
    fill:   "rgba(239, 68, 68, 0.08)",
    hatch:  "rgba(239, 68, 68, 0.55)",
    glow:   "rgba(239, 68, 68, 0.55)",
  },
  removed: {
    stroke: "rgba(59, 130, 246, 0.92)",  // restrained blue
    fill:   "rgba(59, 130, 246, 0.08)",
    hatch:  "rgba(59, 130, 246, 0.55)",
    glow:   "rgba(59, 130, 246, 0.55)",
  },
  modified: {
    stroke: "rgba(234, 179, 8, 0.95)",   // warm amber
    fill:   "rgba(234, 179, 8, 0.08)",
    hatch:  "rgba(234, 179, 8, 0.55)",
    glow:   "rgba(234, 179, 8, 0.55)",
  },
};

export { CLOUD_PALETTE };
